import { RULE_LOG_TYPE } from "./constants";

export const CAMPAIGN_ABO_LOG_TYPE = "Automation";
export const CAMPAIGN_ABO_PARENT_RULE_TYPE = "rule_tracking";
export const CAMPAIGN_ABO_CHILD_RULE_TYPE = "rule_entity_tracking";
export const CAMPAIGN_ABO_LOGS_PARENT_SKELETON_ROWS = 6;
export const CAMPAIGN_ABO_LOGS_CHILD_SKELETON_ROWS = 3;

export const CAMPAIGN_ABO_LOG_COLUMNS = [
  { key: "timestamp", header: "Timestamp", minWidth: 160 },
  // { key: "ruleName", header: "Rule name", minWidth: 140 },
  { key: "ruleType", header: "Rule Type", minWidth: 120 },
  { key: "campaign", header: "Campaign", minWidth: 180 },
  { key: "event", header: "Event", minWidth: 160 },
  { key: "scheduleTrigger", header: "Schedule / trigger", minWidth: 160 },
  { key: "campaignStatus", header: "Campaign Status", minWidth: 150 },
  { key: "budgetInfo", header: "Budget Info", minWidth: 200 },
  { key: "filtersApplied", header: "Filters applied", minWidth: 160 },
  { key: "gapHandling", header: "Gap handling", minWidth: 130 },
  { key: "status", header: "Status", minWidth: 120 },
  { key: "triggeredBy", header: "Triggered By", minWidth: 180 },
];

export function parseLogJsonField(value) {
  if (value == null || value === "" || value === "null") return {};
  if (typeof value === "object") return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function getLogExtraInfo(log) {
  const decision = parseLogJsonField(log?.decision);
  const extraInfoJson = parseLogJsonField(log?.extra_info_json);
  return {
    ...(decision?.extraInfo ?? {}),
    ...extraInfoJson,
  };
}

export function displayLogValue(value) {
  if (value == null) return "";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

const LOG_TIMESTAMP_IST_OPTIONS = {
  timeZone: "Asia/Kolkata",
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: true,
};

/** Parse API UTC timestamp and format for display in IST. */
function normalizeUtcTimestamp(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  if (/[zZ]$/.test(raw) || /[+-]\d{2}:\d{2}$/.test(raw)) return raw;
  if (raw.includes("T")) return raw.endsWith("Z") ? raw : `${raw}Z`;
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(raw)) {
    return `${raw.replace(" ", "T")}Z`;
  }
  return raw;
}

export function formatLogTimestampUtcToIst(value) {
  if (value == null || value === "") return "";
  const normalized = normalizeUtcTimestamp(value);
  if (!normalized) return "";

  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return displayLogValue(value);

  return date.toLocaleString("en-IN", LOG_TIMESTAMP_IST_OPTIONS);
}

function formatBudgetInfoLine(label, value) {
  const displayed = displayLogValue(value);
  return `${label}: ${displayed === "" ? "—" : displayed}`;
}

/** Combined budget fields for a single table column. */
export function formatBudgetInfo(info = {}) {
  return [
    formatBudgetInfoLine("Type", info?.budgetType),
    formatBudgetInfoLine("Base Budget", info?.baseBudget),
    formatBudgetInfoLine("Before", info?.budgetBefore),
    formatBudgetInfoLine("Change Value", info?.budgetValue),
    formatBudgetInfoLine("After", info?.budgetAfter),
  ].join("\n");
}

/** Child logs: before/after status in the Campaign Status column. */
export function formatChildCampaignStatus(info = {}) {
  return [
    formatBudgetInfoLine("Before", info?.beforeStatus),
    formatBudgetInfoLine("After", info?.afterStatus),
  ].join("\n");
}

function mapAboLogBaseFields(log, info) {
  return {
    rowKey: String(log?.id ?? log?.track_id ?? log?.created_at ?? ""),
    timestamp: formatLogTimestampUtcToIst(log?.created_at),
    ruleName: displayLogValue(log?.rule_name),
    ruleType: displayLogValue(info?.ruleType),
    event: displayLogValue(info?.event ?? log?.message),
    scheduleTrigger: displayLogValue(info?.scheduleTrigger),
    triggeredBy: displayLogValue(log?.triggered_by),
    budgetInfo: formatBudgetInfo(info),
    filtersApplied: displayLogValue(info?.filtersApplied ?? info?.filtersAppliedFlag),
    gapHandling: displayLogValue(info?.gapHandling),
    status: displayLogValue(info?.executionStatus ?? log?.effective_change),
    trackId: log?.track_id ?? "",
    raw: log,
  };
}

export function mapAboLogToRow(log, { isChild = false } = {}) {
  const info = getLogExtraInfo(log);
  const base = mapAboLogBaseFields(log, info);

  if (isChild) {
    return {
      ...base,
      campaign: displayLogValue(log?.entity_name ?? info?.effectiveChange),
      campaignStatus: formatChildCampaignStatus(info),
    };
  }

  return {
    ...base,
    campaign: displayLogValue(log?.entity_name ?? info?.effectiveChange),
    campaignStatus: displayLogValue(info?.campaignStatus),
    // Budget details are shown on child (campaign-level) rows only.
    budgetInfo: "",
  };
}

export function buildParentLogsFiltering(ruleName) {
  const filtering = [
    { field_name: "flag_is_scheduled", operator: "NOT_EQUALS", value: null },
    { field_name: "type", operator: "EQUALS", value: RULE_LOG_TYPE },
    { field_name: "flag_is_scheduled", operator: "EQUALS", value: 1 },
    { field_name: "change_recommended", operator: "NOT_EQUALS", value: null },
  ];

  if (ruleName) {
    filtering.push({ field_name: "rule_name", operator: "EQUALS", value: ruleName });
  }

  return filtering;
}

export function buildChildLogsFiltering(trackId) {
  return [{ field_name: "track_id", operator: "EQUALS", value: trackId }];
}

export function buildCampaignAboLogsUrl(clientId, { source, filtering, ruleType }) {
  const params = new URLSearchParams({
    source,
    type: CAMPAIGN_ABO_LOG_TYPE,
    filtering: JSON.stringify(filtering),
    rule_type: ruleType,
  });
  return `${process.env.REACT_APP_ADS_API}/client/${clientId}/logs?${params.toString()}`;
}

export function parseLogsApiList(responseData) {
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData?.data)) return responseData.data;
  return [];
}
